/**
 * Seguridad · Política de sesión — configuración del timeout de inactividad.
 *
 * GAMP 5 / 21 CFR Part 11 §11.10(d): el sistema debe expirar sesiones
 * automáticamente cuando el usuario abandona la estación de trabajo. El
 * timeout es un parámetro de configuración del sistema validado — su cambio
 * debe quedar auditado y requiere razón.
 *
 * Por ahora la edición está deshabilitada — la página es solo lectura.
 */

import { Clock, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  usePoliticaSesionStore,
  TIMEOUT_MIN_MINUTOS,
  TIMEOUT_MAX_MINUTOS,
  WARNING_MIN_MINUTOS,
} from '@/stores/politica-sesion';

export function PoliticaSesionPage() {
  const timeoutMinutos = usePoliticaSesionStore((s) => s.timeoutMinutos);
  const warningMinutos = usePoliticaSesionStore((s) => s.warningMinutos);

  return (
    <Card>
      <CardContent className="space-y-5 pt-5">
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-700" />
          <AlertDescription className="text-xs text-blue-900">
            <b>Cumplimiento GAMP 5 / 21 CFR Part 11 §11.10(d).</b> La sesión expira
            automáticamente tras X minutos sin actividad del usuario (mouse, teclado,
            click, scroll, touch). Antes del cierre se muestra un warning toast.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Timeout principal */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-everex-naranja" />
              <span className="font-semibold">Timeout por inactividad</span>
              <span className="text-[11px] text-gray-600">
                ({TIMEOUT_MIN_MINUTOS}–{TIMEOUT_MAX_MINUTOS} min)
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={timeoutMinutos}
                disabled
                readOnly
                className="w-24 text-center font-mono"
              />
              <span className="text-sm text-gray-600">minutos</span>
            </div>
            <div className="text-[11px] text-gray-600">
              Recomendado regulatorio: <b>30 min</b>.
            </div>
          </div>

          {/* Warning */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="font-semibold">Aviso previo</span>
              <span className="text-[11px] text-gray-600">
                (≥ {WARNING_MIN_MINUTOS} min, &lt; timeout)
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={warningMinutos}
                disabled
                readOnly
                className="w-24 text-center font-mono"
              />
              <span className="text-sm text-gray-600">minutos antes</span>
            </div>
            <div className="text-[11px] text-gray-600">
              Toast de warning con countdown antes del timeout.
            </div>
          </div>
        </div>

        <div className="rounded border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
          <div className="mb-2 font-semibold text-everex-azul">
            ¿Cómo funciona en la práctica?
          </div>
          <ul className="ml-5 list-disc space-y-1 text-[11px]">
            <li>
              El sistema escucha eventos de input (mouse, teclado, click, scroll, touch)
              y registra el <code>lastActivityAt</code>.
            </li>
            <li>Cada 30 segundos verifica cuánto queda hasta el timeout.</li>
            <li>
              <b>{warningMinutos} min antes</b> del timeout, un toast amarillo te avisa
              con countdown. Cualquier acción reanuda el contador.
            </li>
            <li>
              Al cumplirse el timeout: audit con acción{' '}
              <code className="font-mono">sesion.timeout</code>, logout automático y
              redirect a <code className="font-mono">/login</code>.
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
